import PolicyLayout, { H2, P, UL } from "./PolicyLayout";
import { useSettings } from "../../contexts/SettingsContext";

export default function ReturnRefundPolicy() {
  // iter96 / Session C — only the "Cancellations" paragraph reacts to
  // the admin cancellation toggle.  iter101 — the two COD-refund
  // bullets in Section 4 also react to the admin COD toggle so we
  // don't reference COD refunds when COD itself is disabled (no NEW
  // COD orders can be placed → no COD refund path applies going
  // forward).  All other content (returns, damage claims, refund
  // timelines) is preserved verbatim.
  const { customerCancellationEnabled, codEnabled } = useSettings();
  return (
    <PolicyLayout
      title="Return & Refund Policy"
      testid="page-return-refund-policy"
      canonical="/return-refund-policy"
      seoTitle="Return & Refund Policy | Sri Durga Foods"
      seoDescription="Sri Durga Foods return and refund policy — quality guarantee, damage claims, refund timelines, and Shiprocket return process."
    >
      <P>
        At Sri Durga Foods we take great pride in the quality of our
        pickles, pastes, spices and traditional Andhra foods. Because our
        products are perishable food items, returns are handled with care
        and only under specific situations described below. We are
        committed to resolving every legitimate issue quickly and fairly.
      </P>

      <H2>1. Eligible return situations</H2>
      <P>You may raise a return / replacement request in the following cases:</P>
      <UL>
        <li><strong>Damaged product</strong> — the packaging or jar arrived broken, leaking, or tampered with.</li>
        <li><strong>Wrong product</strong> — you received a different item, variant or weight than what you ordered.</li>
        <li><strong>Missing items</strong> — a product or variant from your invoice is missing from the parcel.</li>
        <li><strong>Quality issues</strong> — the product is spoiled, has visible mould, or has an off-odour on receipt (with photo / video evidence).</li>
      </UL>

      <H2>2. Non-returnable situations</H2>
      <UL>
        <li>Products which have been opened, partially used, or consumed (except in the case of genuine quality issues with evidence).</li>
        <li>Reports raised more than 24 hours after delivery.</li>
        <li>Items damaged due to mishandling after delivery.</li>
        <li>Products refused at the time of delivery without a valid reason or returned to origin (RTO) due to non-availability.</li>
        <li>Minor variations in colour, oil-separation, sediment formation, or texture — these are natural for hand-crafted Andhra pickles and not considered defects.</li>
      </UL>

      <H2>3. How to raise a return request</H2>
      <UL>
        <li>Contact us within <strong>24 hours of delivery</strong> via WhatsApp, phone or email.</li>
        <li>Share your <strong>order ID</strong>, a brief description of the issue, and <strong>clear photographs / a short video</strong> of the affected product and outer packaging.</li>
        <li>Our customer-care team will review the request within 1–2 business days and respond with the resolution (replacement, refund, or store credit).</li>
        <li>If a return shipment is required, our shipping partner Shiprocket will arrange the pick-up at no cost to you.</li>
      </UL>

      <H2>4. Refund process</H2>
      <UL>
        <li>Approved refunds for online payments are processed back to the <strong>original payment method via Razorpay</strong>.</li>
        {codEnabled && (
          <li>Refunds for Cash-on-Delivery (COD) orders are processed to your bank account; we will request your bank details securely.</li>
        )}
        <li>
          Refund timelines (after approval): <strong>5–7 business days</strong> for UPI / cards / net-banking
          {codEnabled && (<>; up to <strong>7–10 business days</strong> for COD bank transfers</>)}.
        </li>
        <li>Shipping charges (where applicable) are non-refundable unless the return is due to our error (damaged / wrong / missing).</li>
      </UL>

      <H2>5. Replacements</H2>
      <P>
        Where stock is available, we always prefer to replace a defective
        product with a fresh unit rather than refund. Replacement shipments
        are dispatched within 2 business days of approving the request and
        carry no additional shipping charge to you.
      </P>

      <H2>6. Food product safety considerations</H2>
      <P>
        For your safety, please always:
      </P>
      <UL>
        <li>Inspect the seal and jar before opening.</li>
        <li>Use a clean, dry spoon to serve pickles and pastes.</li>
        <li>Store as per the instructions on the label (away from direct sunlight; refrigerate where indicated).</li>
        <li>Consume within the best-before date printed on the pack.</li>
      </UL>

      <H2>7. Cancellations</H2>
      {customerCancellationEnabled ? (
        <P>
          Orders may be cancelled <strong>any time before they are
          dispatched</strong>, free of charge. You can cancel directly
          from <strong>Your Orders → Order Details</strong>, or contact
          us via WhatsApp, phone or email with your order ID and we will
          stop the shipment and process a full refund. Once the parcel
          has been handed over to the courier and marked as dispatched,
          please follow the return process above instead. See our
          Terms &amp; Conditions for additional details.
        </P>
      ) : (
        <P>
          Self-service order cancellation is currently unavailable on
          the website. If you need to cancel a recent order, please
          contact us via WhatsApp, phone or email with your order ID
          before dispatch and we will do our best to help. Once the
          parcel has been handed over to the courier, please follow the
          return process above instead. See our Terms &amp; Conditions
          for additional details.
        </P>
      )}

      <H2>8. Customer support</H2>
      <P>
        Our customer-care team is available Monday – Saturday, 10 AM – 7
        PM IST. For the fastest response, please reach out via WhatsApp
        using the contact details below — we typically respond within a
        few hours during working time.
      </P>
    </PolicyLayout>
  );
}
